import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, Info, Crown, Users, FileText, Package, UserCircle, Home, Coins, Handshake } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Labels et couleurs pour les modes de tarification
const modeLabels = {
  a_lunite: { label: 'À l\'unité', color: 'bg-blue-100 text-blue-800' },
  au_forfait: { label: 'Au forfait', color: 'bg-green-100 text-green-800' }
};

// Labels pour les profils avec icônes Lucide
const profileLabels = {
  cavalier_proprio: { short: 'Propriétaire', icon: Crown },
  cavalier_pp: { short: 'Propriétaire partagé', icon: Users },
  cavalier_dp: { short: 'Demi-pensionnaire', icon: FileText },
  cavalier_forfait: { short: 'Au forfait', icon: Package },
  cavalier_externe: { short: 'Externe', icon: UserCircle }
};

// Liste des prestations à afficher dans le tableau
const prestationsOrder = [
  { key: 'cours_collectif', label: 'Cours collectif' },
  { key: 'cours_particulier', label: 'Cours particulier' },
  { key: 'stage_demi_journee', label: 'Stage 1/2 journée' },
  { key: 'stage_journee', label: 'Stage 1 journée' },
  { key: 'competition_dressage', label: 'Compétition dressage' },
  { key: 'competition_cso', label: 'Compétition CSO' },
  { key: 'transport_van', label: 'Transport Van' },
  { key: 'cotisation_club', label: 'Cotisation club' },
  { key: 'licence_ffe_moins_18', label: 'Licence FFE moins de 18 ans' },
  { key: 'licence_ffe_plus_18', label: 'Licence FFE 18 ans et plus' },
];

const pensionPrestations = [
  { key: 'pension_box_paille', label: 'Box cheval paille' },
  { key: 'pension_box_copeaux', label: 'Box cheval copeaux' },
  { key: 'pension_pre', label: 'Pré' },
  { key: 'pension_box_poney', label: 'Box poney' },
];

export const PricingRulesTable = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchPricingRules();
  }, []);

  const fetchPricingRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pricing-rules/tarifs-par-profil`);
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des règles tarifaires');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (prix) => {
    if (prix === 0 || prix === null || prix === undefined) return '-';
    return `${prix}€`;
  };

  const getProfilePrestation = (profile, prestationKey) => {
    const prestation = profile.prestations[prestationKey];
    if (!prestation) return { applicable: false };
    return {
      applicable: true,
      prix: prestation.prix,
      label: prestation.label
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-stone-500">
        Impossible de charger les règles tarifaires
      </div>
    );
  }

  const { profiles, tarifs } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Règles tarifaires par profil</h2>
          <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
            <Info className="w-4 h-4" />
            Les tarifs sont appliqués automatiquement selon le rôle du cavalier
          </p>
        </div>
      </div>

      {/* Légende des modes */}
      <div className="flex gap-4 flex-wrap">
        {Object.entries(modeLabels).map(([key, { label, color }]) => (
          <Badge key={key} className={color}>
            {label}
          </Badge>
        ))}
        <Badge className="bg-amber-100 text-amber-800">
          Pension (proprio uniquement)
        </Badge>
      </div>

      {/* Tableau principal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Prestations et tarifs</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="w-48 sticky left-0 bg-stone-50 z-10">Prestation</TableHead>
                {profiles.map((profile) => {
                  const ProfileIcon = profileLabels[profile.role_club]?.icon;
                  return (
                    <TableHead key={profile.role_club} className="text-center min-w-[120px]">
                      <div className="flex flex-col items-center gap-1">
                        {ProfileIcon && <ProfileIcon className="w-5 h-5 text-stone-600" />}
                        <span className="text-xs font-medium">{profileLabels[profile.role_club]?.short || profile.role_club}</span>
                        <Badge className={`text-[10px] ${modeLabels[profile.mode_tarification]?.color}`}>
                          {modeLabels[profile.mode_tarification]?.label}
                        </Badge>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Forfaits */}
              <TableRow className="bg-green-50/50">
                <TableCell className="font-medium sticky left-0 bg-green-50/50 z-10">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    Forfait inclus
                  </span>
                </TableCell>
                {profiles.map((profile) => (
                  <TableCell key={profile.role_club} className="text-center">
                    {profile.forfaits && profile.forfaits.length > 0 ? (
                      <div className="text-xs space-y-1">
                        {profile.forfaits.map((f, idx) => (
                          <div key={idx} className="text-green-700">
                            {f.cours_inclus_par_semaine} cours/sem
                            {f.condition_age && <span className="text-stone-400"> ({f.condition_age === 'moins_18' ? 'moins de 18 ans' : '18 ans et plus'})</span>}
                            {f.prix_trimestriel > 0 && (
                              <span className="ml-1 font-semibold text-green-800">
                                {f.prix_trimestriel}€/trim
                              </span>
                            )}
                          </div>
                        ))}
                        {profile.demi_pension_incluse && (
                          <div className="text-cyan-600 font-medium">+ DP cheval</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-stone-300">-</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>

              {/* Tarifs forfaits trimestriels (détail) */}
              {profiles.some(p => p.forfaits && p.forfaits.length > 0) && (
                <>
                  <TableRow className="bg-green-50/30">
                    <TableCell colSpan={profiles.length + 1} className="py-2">
                      <span className="text-sm font-medium text-green-700 flex items-center gap-2">
                        <Coins className="w-4 h-4" />
                        Tarifs forfaits trimestriels
                      </span>
                    </TableCell>
                  </TableRow>
                  {/* Forfait 1 cours/sem moins de 18 ans */}
                  <TableRow className="hover:bg-stone-50">
                    <TableCell className="font-medium sticky left-0 bg-white z-10 pl-8 text-sm">
                      Forfait 1 cours/sem moins de 18 ans
                    </TableCell>
                    {profiles.map((profile) => {
                      const forfait = profile.forfaits?.find(f => f.tarif_key === 'forfait_1_cours_moins_18');
                      return (
                        <TableCell key={profile.role_club} className="text-center">
                          {forfait ? (
                            <span className="font-medium text-green-700">
                              {forfait.prix_trimestriel > 0 ? `${forfait.prix_trimestriel}€` : <span className="text-amber-600">À définir</span>}
                            </span>
                          ) : (
                            <span className="text-stone-300">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {/* Forfait 1 cours/sem 18 ans et plus */}
                  <TableRow className="hover:bg-stone-50">
                    <TableCell className="font-medium sticky left-0 bg-white z-10 pl-8 text-sm">
                      Forfait 1 cours/sem 18 ans et plus
                    </TableCell>
                    {profiles.map((profile) => {
                      const forfait = profile.forfaits?.find(f => f.tarif_key === 'forfait_1_cours_plus_18');
                      return (
                        <TableCell key={profile.role_club} className="text-center">
                          {forfait ? (
                            <span className="font-medium text-green-700">
                              {forfait.prix_trimestriel > 0 ? `${forfait.prix_trimestriel}€` : <span className="text-amber-600">À définir</span>}
                            </span>
                          ) : (
                            <span className="text-stone-300">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {/* Forfait 2 cours/sem moins de 18 ans */}
                  <TableRow className="hover:bg-stone-50">
                    <TableCell className="font-medium sticky left-0 bg-white z-10 pl-8 text-sm">
                      Forfait 2 cours/sem moins de 18 ans
                    </TableCell>
                    {profiles.map((profile) => {
                      const forfait = profile.forfaits?.find(f => f.tarif_key === 'forfait_2_cours_moins_18');
                      return (
                        <TableCell key={profile.role_club} className="text-center">
                          {forfait ? (
                            <span className="font-medium text-green-700">
                              {forfait.prix_trimestriel > 0 ? `${forfait.prix_trimestriel}€` : <span className="text-amber-600">À définir</span>}
                            </span>
                          ) : (
                            <span className="text-stone-300">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {/* Forfait 2 cours/sem 18 ans et plus */}
                  <TableRow className="hover:bg-stone-50">
                    <TableCell className="font-medium sticky left-0 bg-white z-10 pl-8 text-sm">
                      Forfait 2 cours/sem 18 ans et plus
                    </TableCell>
                    {profiles.map((profile) => {
                      const forfait = profile.forfaits?.find(f => f.tarif_key === 'forfait_2_cours_plus_18');
                      return (
                        <TableCell key={profile.role_club} className="text-center">
                          {forfait ? (
                            <span className="font-medium text-green-700">
                              {forfait.prix_trimestriel > 0 ? `${forfait.prix_trimestriel}€` : <span className="text-amber-600">À définir</span>}
                            </span>
                          ) : (
                            <span className="text-stone-300">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </>
              )}

              {/* Prestations standard */}
              {prestationsOrder.map((prestation) => (
                <TableRow key={prestation.key} className="hover:bg-stone-50">
                  <TableCell className="font-medium sticky left-0 bg-white z-10">
                    {prestation.label}
                  </TableCell>
                  {profiles.map((profile) => {
                    const p = getProfilePrestation(profile, prestation.key);
                    return (
                      <TableCell key={profile.role_club} className="text-center">
                        {p.applicable ? (
                          <span className="font-medium text-stone-700">
                            {formatPrice(p.prix)}
                          </span>
                        ) : (
                          <span className="text-stone-300">-</span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Séparateur Pension */}
              <TableRow className="bg-amber-50/50">
                <TableCell colSpan={profiles.length + 1} className="py-2">
                  <span className="text-sm font-medium text-amber-700 flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Pension (propriétaires uniquement)
                  </span>
                </TableCell>
              </TableRow>

              {/* Pensions */}
              {pensionPrestations.map((prestation) => (
                <TableRow key={prestation.key} className="hover:bg-stone-50">
                  <TableCell className="font-medium sticky left-0 bg-white z-10 pl-8">
                    {prestation.label}
                  </TableCell>
                  {profiles.map((profile) => {
                    const p = getProfilePrestation(profile, prestation.key);
                    const isPensionApplicable = profile.pension_applicable;
                    return (
                      <TableCell key={profile.role_club} className="text-center">
                        {isPensionApplicable && p.applicable ? (
                          <span className="font-medium text-amber-700">
                            {formatPrice(p.prix)}/mois
                          </span>
                        ) : (
                          <span className="text-stone-300 text-xs">N/A</span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Demi-pension */}
              <TableRow className="hover:bg-stone-50 border-t-2">
                <TableCell className="font-medium sticky left-0 bg-white z-10">
                  <span className="flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-cyan-600" />
                    Demi-pension cheval
                  </span>
                </TableCell>
                {profiles.map((profile) => (
                  <TableCell key={profile.role_club} className="text-center">
                    {profile.demi_pension_incluse ? (
                      <Badge className="bg-cyan-100 text-cyan-800">
                        Inclus
                      </Badge>
                    ) : profile.role_club === 'cavalier_proprio' || profile.role_club === 'cavalier_pp' ? (
                      <span className="text-stone-300 text-xs">N/A</span>
                    ) : (
                      <span className="text-stone-300">-</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Notes explicatives */}
      <Card>
        <CardContent className="pt-4">
          <h3 className="font-medium text-stone-700 mb-3">Légende</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Crown className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Propriétaire</strong>: Possède son propre cheval. Tarif préférentiel sur les cours. Pension facturée mensuellement.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Users className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Propriétaire partagé</strong>: Copropriétaire d'un cheval. Tarif préférentiel. <span className="text-amber-600 font-medium">Pas de pension</span> (facturée au propriétaire principal).
                </div>
              </div>
              <div className="flex items-start gap-2">
                <FileText className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Demi-pensionnaire</strong>: Forfait mensuel incluant 2 cours/semaine + demi-pension cheval. Cours supplémentaires facturés.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Package className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Au forfait</strong>: Forfait trimestriel (1 ou 2 cours/semaine selon formule et âge). Cours supplémentaires facturés.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <UserCircle className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Externe</strong>: Cavalier occasionnel. Tous les cours sont facturés à l'unité au tarif standard.
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricingRulesTable;
